// Расчёт «умного подбора утеплителя»: цена за м³, фильтры, самое дешёвое.
//
// Здесь нет ни JSX, ни запросов — только арифметика и отбор, потому что именно
// их проверяют тесты (DOM-библиотеки в проекте нет). Компоненты подбора берут
// готовые строки и раскладывают их по экрану.
//
// Каталог приходит из `options` — того же справочника, из которого работает
// расчёт. Поэтому цена в таблице и цена в строке заказа совпадают по
// построению, а не по совпадению: см. src/utils/insulationCatalog.js.
//
// Цена за м³ — единственная величина, по которой подбор решает, что дешевле,
// поэтому объём упаковки берётся не из поля справочника, а из голосования
// нескольких источников: см. src/utils/insulationVolume.js.

import { resolveInsulationVolume } from '../../utils/insulationVolume.js';
import {
  buildApplicationOptions,
  groupKeysForRow,
  splitApplications,
} from '../../utils/insulationApplications.js';
import {
  INSULATION_KIND,
  KIND_LABELS,
  insulationKindOf,
  isInsulationOption,
} from '../../utils/insulationCatalog.js';

export { INSULATION_KIND, KIND_LABELS, MEASURE_LABELS } from '../../utils/insulationCatalog.js';

/** Характеристика, по которой фильтруется вид материала. */
export const measureOf = (row) => (
  row.kind === INSULATION_KIND.EXTRUDED ? row.compression : row.density
);

const toPositive = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Плотность в справочнике — текст, и попадается мусор вида «37; 90».
// parseFloat вернул бы 37 молча; такие значения честнее считать пустыми, чтобы
// они не сдвигали границы фильтра.
const toStrictPositive = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(str)) return null;
  return toPositive(str);
};

// Предельный возраст цены. Старше — позиции в подборе нет вовсе.
//
// Раньше порог только помечал строку как устаревшую, а показывались все. Теперь
// отсев жёсткий: менеджер отдаёт клиенту цену из подбора, и она обязана быть
// той, по которой сегодня продают. Цена старше двух недель этого не гарантирует.
//
// Отсев дорогой: из 727 номенклатур утеплителя в справочнике свежую цену имеют
// около 146, то есть список короче впятеро. Это не потеря данных, а отказ
// показывать то, чем нельзя пользоваться; дата обновления видна у каждой строки.
export const MAX_PRICE_AGE_DAYS = 14;

// Дата в справочнике — строка «ДД.ММ.ГГГГ». Разбираем её вручную: Date.parse
// прочитал бы «05.09.2026» как 9 мая по американскому порядку.
const daysSincePriceUpdate = (value) => {
  const match = String(value || '').trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - date.getTime()) / (24 * 60 * 60 * 1000));
};

// В наборе `optionsByPayment.noncash` самый дешёвый поставщик выбран по
// безналичной цене — её и показываем, иначе таблица покажет цену другого
// поставщика. 'card' — то же безналичный расчёт, так его называет Products.
const priceFieldFor = (paymentMethod) => (
  paymentMethod === 'card' || paymentMethod === 'noncash' || paymentMethod === 'nonCash'
    ? 'PRICE_NONCASH'
    : 'PRICE_CASH'
);

/**
 * Строит строки подбора из справочника расчёта.
 *
 * @param {object} params
 * @param {object} params.options       — словарь `options` из OptionsContext
 * @param {string} params.paymentMethod — форма оплаты, как её знает Products
 */
export const buildPickerRows = ({ options, paymentMethod = 'cash' } = {}) => {
  if (!options || typeof options !== 'object') return [];

  const priceField = priceFieldFor(paymentMethod);

  return Object.entries(options)
    .filter(([name, data]) => isInsulationOption(name, data))
    // Позиция без цены или со старой ценой в подбор не попадает. Проверка
    // стоит до разбора строки: от набора строк считаются и счётчики фильтров,
    // и границы диапазона, и они должны описывать ровно то, что видно.
    .filter(([, data]) => {
      if (!toPositive(data[priceField])) return false;
      const age = daysSincePriceUpdate(data.PRICE_UPDATE_DATE);
      return age !== null && age <= MAX_PRICE_AGE_DAYS;
    })
    .map(([name, data]) => {
      const { volume, source, isDisputed } = resolveInsulationVolume(name, data.VOLUME_CUBIC);
      const price = toPositive(data[priceField]);
      const thicknessMm = toStrictPositive(data.INSULATION_THICKNESS);

      // Спорный объём в подборе не считается объёмом вовсе.
      //
      // resolveInsulationVolume при разногласии возвращает лучшее из доступных —
      // для справки этого хватает, для цены за куб нет. Ошибка здесь
      // несимметрична: заниженный объём завышает цену за куб и уводит товар
      // вниз, а завышенный — занижает её и выводит товар в «самое дешёвое».
      // На московском прайсе так и вышло: у «Кнауф Инсулейшн защита от шума
      // 100 х 610 х 1230 мм» площадь в названии указана как 14,4 м², хотя 12
      // плит 610×1230 дают 9,0 м². Объём завысился до 1,44 м³ вместо 0,9, цена
      // за куб упала вдвое, и строка возглавила таблицу.
      //
      // Поэтому: объём неизвестен — цены за куб нет, прочерк и пометка.
      const hasVolume = volume > 0 && !isDisputed;

      // Цена здесь уже есть — без неё строка отсеяна выше. А вот объёма может
      // не быть, и тогда цены за куб нет: прочерк и пометка.
      const pricePerM3 = hasVolume ? price / volume : null;

      return {
        id: name,
        name,
        manufacturer: data.MANUFACTURER || null,
        source: data.SOURCE || null,
        category: data.CATEGORY || null,
        priceUpdateDate: data.PRICE_UPDATE_DATE || null,
        unit: data.BASE_UNIT || null,
        density: toStrictPositive(data.DENSITY),
        compression: toStrictPositive(data.COMPRESSION),
        thickness: thicknessMm,
        kind: insulationKindOf(name),
        volume: hasVolume ? volume : 0,
        // Площадь упаковки считаем из объёма и толщины, а не берём готовым
        // полем: объём уже выверен голосованием, а отдельная площадь в данных
        // врёт там же, где врал объём (у «Кнауф Инсулейшн защита от шума»
        // записано 14,4 м² вместо 9,0). Деление сходится с названием до третьего
        // знака на всех проверенных позициях.
        area: hasVolume && thicknessMm > 0 ? volume / (thicknessMm / 1000) : 0,
        volumeSource: source,
        isVolumeDisputed: isDisputed,
        price,
        pricePerM3,
        applicationKeys: groupKeysForRow(data.ANALOGY),
        applicationLabels: splitApplications(data.ANALOGY),
      };
    });
};

/** Толщины, реально встречающиеся в отобранных строках, по возрастанию. */
export const buildThicknessOptions = (rows) => {
  const values = new Set();
  (rows || []).forEach((row) => {
    if (row.thickness > 0) values.add(row.thickness);
  });
  return [...values].sort((a, b) => a - b);
};

/** Границы третьего фильтра — из данных, а не из справочника допусков. */
export const buildMeasureBounds = (rows) => {
  const values = (rows || []).map(measureOf).filter((value) => value > 0);
  if (values.length === 0) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
};

/** Пункты фильтра «по применению» для уже отобранного вида материала. */
export const buildApplicationFilterOptions = (rows) => buildApplicationOptions(rows || []);

/**
 * Выбранные сейчас фильтры — одной плоской лентой над таблицей.
 *
 * Список применений зажат в прокрутку: их два десятка, и целиком они выдавливают
 * таблицу за экран. Побочный эффект — выбранный чип уезжает из видимой части, и
 * менеджер, задав плотность, не видит, что сверху висит применение из прошлого
 * поиска. Таблица пустеет «без причины».
 *
 * Поэтому всё выбранное показывается отдельно и всегда на виду, с возможностью
 * снять по одному.
 */
export const buildActiveFilters = ({
  applicationKeys = [],
  applicationOptions = [],
  thicknesses = [],
  range = null,
  measure = null,
  kind = null,
  search = '',
} = {}) => {
  const filters = [];

  // Вид материала стоит первым и снять его нельзя — он выбран всегда. В ленте
  // он нужен потому, что именно он обнуляет остальные фильтры при смене, и в
  // пустой таблице «минвата» против «ЭППС» объясняет ноль строк не хуже
  // применения.
  if (kind) {
    filters.push({ type: 'kind', key: kind, label: KIND_LABELS[kind] || kind, removable: false });
  }

  applicationKeys.forEach((key) => {
    const option = applicationOptions.find((item) => item.key === key);
    filters.push({ type: 'application', key, label: option ? option.label : key, removable: true });
  });

  [...thicknesses].sort((a, b) => a - b).forEach((value) => {
    filters.push({ type: 'thickness', key: value, label: `${value} мм`, removable: true });
  });

  const from = toPositive(range && range.from);
  const to = toPositive(range && range.to);
  if (from || to) {
    const unit = measure ? measure.unit : '';
    const title = measure ? measure.title : 'Диапазон';
    const value = from && to ? `${from}–${to}` : (from ? `от ${from}` : `до ${to}`);
    filters.push({ type: 'range', key: 'range', label: `${title}: ${value} ${unit}`.trim(), removable: true });
  }

  // Поиск — такой же фильтр, как остальные, и в ленте он нужен по той же
  // причине: набранное слово прокручивается за пределы видимой части экрана, а
  // таблица остаётся пустой без объяснений.
  const query = String(search || '').trim();
  if (query) {
    filters.push({ type: 'search', key: 'search', label: `Поиск: ${query}`, removable: true });
  }

  return filters;
};

// Поиск по таблице: регистр и лишние пробелы значения не имеют, «ё» и «е» —
// одно и то же. Названия в справочнике пишут и так, и так («Изовер» против
// «Изовёр»), а менеджер набирает по памяти.
export const normalizeSearch = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/ё/g, 'е')
  .replace(/\s+/g, ' ');

/**
 * Подходит ли строка под поисковый запрос.
 *
 * Слова ищутся по отдельности и в любом порядке: «роквул 100» должно находить
 * «Утеплитель Роквул Руф Баттс Оптима 100 мм», где между словами стоит ещё
 * половина названия. Поиск идёт и по производителю — по нему ищут не реже, чем
 * по названию, а в названии он есть не всегда.
 */
const matchesSearch = (row, query) => {
  const haystack = normalizeSearch(`${row.name || ''} ${row.manufacturer || ''}`);
  return query.split(' ').every((word) => haystack.includes(word));
};

const matchesRange = (value, range) => {
  const from = toPositive(range && range.from);
  const to = toPositive(range && range.to);
  if (!from && !to) return true;

  // Диапазон задан, а характеристики у строки нет — под него она не подходит.
  // Скрывать такую строку молча можно только при незаданном диапазоне.
  const actual = toPositive(value);
  if (!actual) return false;

  if (from && actual < from) return false;
  if (to && actual > to) return false;
  return true;
};

/**
 * Отбор строк по трём фильтрам задания.
 *
 * Пустой фильтр не отбирает ничего — это «все», а не «ни одного».
 */
export const filterPickerRows = ({
  rows,
  kind = INSULATION_KIND.MINERAL,
  applicationKeys = [],
  thicknesses = [],
  range = null,
  search = '',
} = {}) => {
  const applications = new Set(applicationKeys);
  const thicknessSet = new Set((thicknesses || []).map(toPositive).filter(Boolean));
  const query = normalizeSearch(search);

  return (rows || []).filter((row) => {
    if (row.kind !== kind) return false;

    if (query && !matchesSearch(row, query)) return false;

    if (applications.size > 0 && !(row.applicationKeys || []).some((key) => applications.has(key))) {
      return false;
    }

    if (thicknessSet.size > 0 && !thicknessSet.has(row.thickness)) {
      return false;
    }

    return matchesRange(measureOf(row), range);
  });
};

export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';

/**
 * По какому столбцу сортируем.
 *
 * «Итого» и «Сумма» считаются от потребности, поэтому пока она не задана,
 * сортировать по ним нечего: rowDemand вернёт null у всех строк разом.
 */
export const SORT_COLUMNS = {
  PRICE_PER_M3: 'pricePerM3',
  TOTAL_VOLUME: 'totalVolume',
  TOTAL_PRICE: 'totalPrice',
};

/** Столбцы, которые без заданной потребности сортировать нечем. */
export const sortNeedsDemand = (column) => (
  column === SORT_COLUMNS.TOTAL_VOLUME || column === SORT_COLUMNS.TOTAL_PRICE
);

/**
 * Порядок строк. По умолчанию — по возрастанию цены за м³: задание требует,
 * чтобы самый дешёвый товар был виден при любом наборе фильтров.
 *
 * Сортировать можно и по «Итого» (объём под потребность), и по «Сумме»
 * (деньги под неё же). Это разные ответы: кратность округляет каждую строку
 * вверх по-своему, и товар с дешёвым кубом, но крупной упаковкой на 1000 м²
 * выходит и дороже, и объёмнее соседа с упаковкой помельче.
 *
 * Строки без значения всегда внизу, в каком бы направлении ни сортировали:
 * иначе при сортировке по убыванию таблицу возглавили бы прочерки.
 */
export const sortPickerRows = (
  rows,
  direction = SORT_ASC,
  column = SORT_COLUMNS.PRICE_PER_M3,
  demand = null
) => {
  // Ключ считается по разу на строку, а не на каждое сравнение: rowDemand
  // пересчитывает кратность, и внутри сортировки это вышло бы n·log n вызовов.
  const keyed = (rows || []).map((row) => {
    if (column === SORT_COLUMNS.PRICE_PER_M3) return { row, key: row.pricePerM3 };

    const totals = rowDemand(row, demand);
    if (!totals || !totals.packages) return { row, key: null };

    const value = column === SORT_COLUMNS.TOTAL_PRICE ? totals.totalPrice : totals.totalVolume;
    return { row, key: value > 0 ? value : null };
  });

  keyed.sort((a, b) => {
    if (a.key === null && b.key === null) return 0;
    if (a.key === null) return 1;
    if (b.key === null) return -1;
    return direction === SORT_DESC ? b.key - a.key : a.key - b.key;
  });

  return keyed.map((item) => item.row);
};

/** Самая дешёвая строка по цене за м³. null — если сравнивать не с чем. */
export const cheapestRow = (rows) => (
  (rows || []).reduce((best, row) => {
    if (row.pricePerM3 === null) return best;
    if (!best || row.pricePerM3 < best.pricePerM3) return row;
    return best;
  }, null) || null
);

/**
 * Самый дешёвый товар по каждому применению — первый пункт задания.
 *
 * Сводка считается по строкам, уже прошедшим фильтры толщины и плотности:
 * менеджер задал условия, и «самое дешёвое» обязано их учитывать, иначе сводка
 * над таблицей противоречила бы самой таблице.
 *
 * Возвращает по строке на группу применений, включая пустые: пункт, под который
 * товаров нет, показывается с прочерком, а не исчезает.
 */
export const buildApplicationDigest = ({ rows } = {}) => (
  buildApplicationFilterOptions(rows || []).map((option) => {
    const matching = (rows || []).filter((row) => (row.applicationKeys || []).includes(option.key));
    return {
      key: option.key,
      label: option.label,
      count: matching.length,
      // Сколько из них с ценой. Без этого числа строка «11 позиций — нет
      // самого дешёвого» выглядит противоречиво: товары под применение есть,
      // просто у всех цена обнулена по сроку давности.
      pricedCount: matching.filter((row) => row.pricePerM3 !== null).length,
      cheapest: cheapestRow(matching),
      // Общий объём всего, что подходит под применение, — по нему видно, широкий
      // это выбор или единственная позиция в прайсе.
      totalVolume: matching.reduce((sum, row) => sum + (row.volume || 0), 0),
    };
  })
);

/** В чём менеджер задаёт потребность. */
export const DEMAND_UNITS = { PACK: 'pack', AREA: 'm2', VOLUME: 'm3' };

export const DEMAND_UNIT_LABELS = {
  [DEMAND_UNITS.PACK]: 'упак',
  [DEMAND_UNITS.AREA]: 'м²',
  [DEMAND_UNITS.VOLUME]: 'м³',
};

/**
 * Очистка ввода потребности: остаётся одно число с одной десятичной точкой.
 *
 * Раньше чистка была двумя заменами подряд, и обе оказались дырявыми. Запятая
 * менялась без флага `g`, то есть только первая. А лишние точки убирал шаблон
 * `(\..*)\.` — из-за жадности он съедал ровно одну: «1.2.3.4» превращалось в
 * «1.2.34», всё ещё не число. При наборе руками это не всплывало (значение
 * нормализуется на каждый символ), а при вставке из буфера — да.
 *
 * Поэтому не «убрать лишнее», а собрать заново: целая часть и всё остальное
 * без разделителей.
 */
export const sanitizeDemandValue = (value) => {
  const normalized = String(value ?? '').replace(/,/g, '.').replace(/[^\d.]/g, '');
  const [whole, ...rest] = normalized.split('.');
  return rest.length > 0 ? `${whole}.${rest.join('')}` : whole;
};

/**
 * Почему строку нельзя пересчитать под потребность — словами для менеджера.
 *
 * «Нечем пересчитать» в таблице выглядит как дыра непонятного происхождения.
 * Причина всегда в справочнике, и назвать её надо прямо: у 8% позиций
 * утеплителя не заполнена толщина, а без неё нет площади упаковки.
 */
export const demandBlockReason = (row, demand) => {
  if (!row) return null;
  const unit = (demand && demand.unit) || DEMAND_UNITS.PACK;

  if (!(row.volume > 0)) {
    return row.isVolumeDisputed
      ? 'объём упаковки в справочнике противоречит названию'
      : 'в справочнике не заполнен объём упаковки';
  }
  if (unit === DEMAND_UNITS.AREA && !(row.area > 0)) {
    return 'в справочнике не заполнена толщина — площадь упаковки неизвестна';
  }
  return null;
};

/** Сколько выбранной меры в одной упаковке. 0 — мерить нечем. */
const perPackage = (row, unit) => {
  if (unit === DEMAND_UNITS.PACK) return 1;
  if (unit === DEMAND_UNITS.VOLUME) return row && row.volume > 0 ? row.volume : 0;
  return row && row.area > 0 ? row.area : 0;
};

/**
 * Что получится у строки под заданную потребность.
 *
 * Утеплитель продаётся упаковками, поэтому дробной упаковки не бывает: нужное
 * количество округляется ВВЕРХ до целой. Просит менеджер 1000 м² — берём 1009,
 * и это ровно та кратность, о которой он должен узнать до отправки КП, а не
 * после. Поэтому возвращается и `delivered` (сколько выйдет на самом деле), и
 * `overshoot` (насколько больше просимого).
 *
 * @returns null — потребность не задана; иначе объект, где packages === null
 *          означает «этой мерой строку не пересчитать» (нет объёма или толщины).
 */
export const rowDemand = (row, demand) => {
  if (!row) return null;

  const requested = toPositive(demand && demand.value);
  if (!requested) return null;

  const unit = (demand && demand.unit) || DEMAND_UNITS.PACK;
  const per = perPackage(row, unit);
  if (!per) {
    return { requested, unit, packages: null, delivered: 0, overshoot: 0, totalVolume: 0, totalArea: 0, totalPrice: 0 };
  }

  const packages = Math.max(1, Math.ceil(requested / per));
  const delivered = packages * per;

  return {
    requested,
    unit,
    packages,
    delivered,
    overshoot: delivered - requested,
    totalVolume: row.volume > 0 ? packages * row.volume : 0,
    totalArea: row.area > 0 ? packages * row.area : 0,
    totalPrice: row.price > 0 ? packages * row.price : 0,
  };
};

/**
 * Самое дешёвое под заданную потребность — по итоговой сумме, а не по цене за куб.
 *
 * Это разные ответы. Кратность округляет каждую строку вверх по-своему: товар с
 * дешёвым кубом, но крупной упаковкой, на 1000 м² может выйти дороже соседа с
 * упаковкой помельче. Пока потребность не задана, сравнивать нечем кроме цены за
 * куб — тогда работает cheapestRow.
 */
export const cheapestForDemand = (rows, demand) => {
  let best = null;
  let bestPrice = Infinity;

  (rows || []).forEach((row) => {
    const totals = rowDemand(row, demand);
    if (!totals || !totals.packages || !(totals.totalPrice > 0)) return;
    if (totals.totalPrice < bestPrice) {
      best = row;
      bestPrice = totals.totalPrice;
    }
  });

  return best;
};

export default buildPickerRows;
